import {Scroller} from "primeng/scroller";

const MAX_VIEW_INIT_RETRY_FRAMES = 30;

/**
 * Workaround for PrimeNG 21.1.6 (remove once fixed upstream).
 *
 * PrimeNG's Scroller (the virtual scroll inside p-select / p-multiselect panels)
 * sets itself up once, in viewInit(), and only if its element is visible then.
 * The panel is mounted with display:none and shown a frame later by p-motion, so
 * when frames lag (a busy machine) viewInit() finds it hidden and gives up. It is
 * only retried on the next change detection of the OnPush dropdown, which can be
 * seconds away, and until then the open list is an empty listbox.
 *
 * Retry on the following animation frames until the element is visible. The
 * dropdown's scrollToIndex() to the selected option on open is a no-op on a
 * scroller that is not set up yet, so it is kept and replayed after setup, so
 * aria-activedescendant points at a rendered option.
 */
export function patchScrollerViewInit(): void {
  const proto: any = Scroller.prototype;
  if (proto.__patchedViewInit) return;
  if (typeof proto.viewInit !== "function" || typeof proto.scrollToIndex !== "function" || typeof proto.onDestroy !== "function") {
    console.warn("patchScrollerViewInit: PrimeNG Scroller has changed; the empty virtual-scroll list workaround is not applied.");
    return;
  }
  const origViewInit = proto.viewInit;
  const origScrollToIndex = proto.scrollToIndex;
  const origOnDestroy = proto.onDestroy;

  proto.viewInit = function (this: any) {
    const wasInitialized = this.initialized;
    origViewInit.call(this);
    if (this.initialized) {
      this.__viewInitRetries = 0;
      if (!wasInitialized && this.__pendingScrollToIndex) {
        const args = this.__pendingScrollToIndex;
        this.__pendingScrollToIndex = null;
        // after the setTimeout in which init() lays out the items
        setTimeout(() => !this.__destroyed && this.scrollToIndex(...args), 1);
      }
      return;
    }
    const el: HTMLElement | undefined = this.elementViewChild?.nativeElement;
    const retries = this.__viewInitRetries ?? 0;
    if (this.__destroyed || !el?.isConnected || this.__viewInitRetryFrame || retries >= MAX_VIEW_INIT_RETRY_FRAMES) return;
    this.__viewInitRetries = retries + 1;
    // init() calls detectChanges() on the scroller itself, so the frames need no app tick
    const schedule = () => {
      this.__viewInitRetryFrame = requestAnimationFrame(() => {
        this.__viewInitRetryFrame = null;
        if (!this.__destroyed) this.viewInit();
      });
    };
    this.zone ? this.zone.runOutsideAngular(schedule) : schedule();
  };

  proto.scrollToIndex = function (this: any, ...args: any[]) {
    if (!this.initialized) {
      this.__pendingScrollToIndex = args;
      return;
    }
    return origScrollToIndex.apply(this, args);
  };

  proto.onDestroy = function (this: any) {
    this.__destroyed = true;
    if (this.__viewInitRetryFrame) {
      cancelAnimationFrame(this.__viewInitRetryFrame);
      this.__viewInitRetryFrame = null;
    }
    this.__pendingScrollToIndex = null;
    return origOnDestroy.call(this);
  };

  proto.__patchedViewInit = true;
}
